import { useCallback, useRef } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import './TiltedCard.css';

export interface TiltCardProps {
  children: ReactNode;
  className?: string;
  /** Maximum tilt angle in degrees, applied at the card's edges. */
  rotateAmplitude?: number;
  /** Perspective distance in px — lower values feel more dramatic. */
  perspective?: number;
}

export default function TiltedCard({
  children,
  className,
  rotateAmplitude = 12,
  perspective = 900,
}: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  const canTilt = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const node = ref.current;
      if (!node || !canTilt()) return;

      const rect = node.getBoundingClientRect();
      const percentX = (e.clientX - rect.left) / rect.width - 0.5;
      const percentY = (e.clientY - rect.top) / rect.height - 0.5;

      const rotateY = percentX * rotateAmplitude * 2;
      const rotateX = -percentY * rotateAmplitude * 2;

      node.style.setProperty('--tilt-rx', `${rotateX.toFixed(2)}deg`);
      node.style.setProperty('--tilt-ry', `${rotateY.toFixed(2)}deg`);
    },
    [canTilt, rotateAmplitude],
  );

  const handleMouseLeave = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    node.style.setProperty('--tilt-rx', '0deg');
    node.style.setProperty('--tilt-ry', '0deg');
  }, []);

  return (
    <div
      ref={ref}
      className={['tilt-card', className].filter(Boolean).join(' ')}
      style={{ '--tilt-perspective': `${perspective}px` } as CSSProperties}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="tilt-card__inner">{children}</div>
    </div>
  );
}
